/**
 * Emergency contacts: name + phone required, one primary contact. The first contact becomes
 * primary automatically; marking another one primary demotes the rest (server-side).
 * Self: /me/emergency-contacts. Admin: /{memberId}/emergency-contacts.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlus, IconEdit, IconTrash, IconAlertCircle, IconPhoneCall, IconStar } from '@tabler/icons-react';
import { Card, Stack, Group, Button, TextInput, SimpleGrid, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, Badge, Switch, ThemeIcon, EmptyState } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import type { MemberEmergencyContact } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useInvalidateMember } from '../useMemberMutations';
import { errorMessage } from '../memberFormat';

interface ContactForm { name: string; relationship: string; phone: string; altPhone: string; isPrimary: boolean }
const EMPTY: ContactForm = { name: '', relationship: '', phone: '', altPhone: '', isPrimary: false };

export function EmergencyContactsTab({ api, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateMember(api);
  const contacts = useQuery({ queryKey: api.keys.emergency, queryFn: () => api.emergency.list() });
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [form, setForm] = useState<ContactForm>(EMPTY);

  const refresh = () => { queryClient.invalidateQueries({ queryKey: api.keys.emergency }); invalidate(); };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        // On update "" clears these; on create blank just means none.
        relationship: form.relationship.trim(),
        altPhone: form.altPhone.trim(),
        isPrimary: form.isPrimary,
      };
      return editing?.id ? api.emergency.update(editing.id, body) : api.emergency.add(body);
    },
    onSuccess: () => { toast.success('Saved', editing?.id ? 'Contact updated.' : 'Contact added.'); setEditing(null); refresh(); },
    onError: (e) => toast.error('Could not save the contact', errorMessage(e)),
  });

  const makePrimary = useMutation({
    mutationFn: (c: MemberEmergencyContact) => api.emergency.update(c.emergencyContactID, { isPrimary: true }),
    onSuccess: () => { toast.success('Primary contact changed'); refresh(); },
    onError: (e) => toast.error('Could not change the primary contact', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.emergency.remove(id),
    onSuccess: () => { toast.success('Removed', 'Contact removed.'); refresh(); },
    onError: (e) => toast.error('Could not remove the contact', errorMessage(e)),
  });

  const list = [...(contacts.data ?? [])].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name));
  const openAdd = () => { setForm({ ...EMPTY, isPrimary: list.length === 0 }); save.reset(); setEditing({ id: null }); };
  const openEdit = (c: MemberEmergencyContact) => {
    setForm({ name: c.name, relationship: c.relationship ?? '', phone: c.phone, altPhone: c.altPhone ?? '', isPrimary: c.isPrimary });
    save.reset();
    setEditing({ id: c.emergencyContactID });
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Emergency contacts</Text>
            <Text size="sm" c="dimmed">Who to call if something happens {api.mode === 'self' ? 'to you' : 'to this person'} at church.</Text>
          </div>
          {perms.edit && <Button size="sm" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add contact</Button>}
        </Group>

        {contacts.isLoading && <Stack gap="sm"><Skeleton height={70} /><Skeleton height={70} /></Stack>}
        {contacts.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load contacts">{errorMessage(contacts.error)}</Alert>}
        {!contacts.isLoading && !contacts.isError && list.length === 0 && (
          <EmptyState size="sm" icon={<IconPhoneCall size={32} />} title="No emergency contacts"
            description={perms.edit ? 'Add at least one person the church can call in an emergency.' : 'No emergency contact has been recorded.'}
            actionLabel={perms.edit ? 'Add contact' : undefined} onAction={perms.edit ? openAdd : undefined} />
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {list.map((c) => (
            <Card key={c.emergencyContactID} withBorder padding="md" radius="md">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" color={c.isPrimary ? 'orange' : 'gray'} size="lg"><IconPhoneCall size={18} /></ThemeIcon>
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap={6}>
                      <Text fw={600} size="sm">{c.name}</Text>
                      {c.isPrimary && <Badge size="xs" color="orange" variant="light" leftSection={<IconStar size={10} />}>Primary</Badge>}
                    </Group>
                    {c.relationship && <Text size="xs" c="dimmed">{c.relationship}</Text>}
                    <Text size="sm">{c.phone}</Text>
                    {c.altPhone && <Text size="sm" c="dimmed">{c.altPhone}</Text>}
                  </Stack>
                </Group>
                {perms.edit && (
                  <Group gap={4} wrap="nowrap">
                    {!c.isPrimary && (
                      <Tooltip label="Make primary"><ActionIcon variant="subtle" color="orange" aria-label="Make primary" loading={makePrimary.isPending} onClick={() => makePrimary.mutate(c)}><IconStar size={16} /></ActionIcon></Tooltip>
                    )}
                    <Tooltip label="Edit"><ActionIcon variant="subtle" aria-label="Edit contact" onClick={() => openEdit(c)}><IconEdit size={16} /></ActionIcon></Tooltip>
                    <Tooltip label="Remove">
                      <ActionIcon variant="subtle" color="red" aria-label="Remove contact"
                        onClick={() => modal.confirm({
                          title: `Remove ${c.name}?`,
                          body: c.isPrimary && list.length > 1 ? 'The next contact becomes the primary contact.' : 'This contact will be removed from the profile.',
                          confirmLabel: 'Remove',
                          confirmColor: 'red',
                          onConfirm: () => remove.mutate(c.emergencyContactID),
                        })}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit emergency contact' : 'Add emergency contact'} centered size="md">
        <Stack gap="md">
          <TextInput label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <TextInput label="Relationship" placeholder="For example Mother, Spouse or Neighbour" value={form.relationship}
            onChange={(e) => setForm({ ...form, relationship: e.currentTarget.value })} />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })} />
            <TextInput label="Alternative phone" value={form.altPhone} onChange={(e) => setForm({ ...form, altPhone: e.currentTarget.value })} />
          </SimpleGrid>
          <Switch label="Primary contact" description="The first person to call." checked={form.isPrimary}
            onChange={(e) => setForm({ ...form, isPrimary: e.currentTarget.checked })} />
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={save.isPending} disabled={!form.name.trim() || !form.phone.trim()} onClick={() => save.mutate()}>Save contact</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
